import type { Workflow } from '@toolproof-core/genesis/_contract/_index';

import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import { mutableWorkflowToWorkflow } from '@toolproof-core/lib/_pure/workflow-canonicalization';

import type { SpaceOverlayIntent } from '@toolproof-core/visualization/runtime/_contract/overlay';

import { ACTION_FREE, ACTION_OVERVIEW, requestWorkflowOverview } from '@/workflow/_pure/derivation/overviewNavigation';
import { requireConfig } from '@/workflow-builder/_pure/config/config';
import {
    ACTION_BIND_BOOLEAN_TRUE,
    ACTION_DISCARD_WORKFLOW,
    ACTION_RUN_REMOTE,
    ACTION_SUBMIT_RUNTIME_FALSE,
    ACTION_SUBMIT_RUNTIME_NATURAL,
    ACTION_SUBMIT_RUNTIME_TRUE,
    resolveActionFromOverlayActionId,
    SECTION_RUNTIME_INPUT,
} from '@/workflow-builder/_pure/interaction/actionMapping';
import {
    getSelectedBindingTargetGuardError,
    isBooleanBindingAction,
    isNaturalBindingAction,
} from '@/workflow-builder/_pure/interaction/overlayIntentGuards';
import {
    buildOverlayModel,
} from '@/workflow-builder/_pure/interaction/overlayModel';
import { selectBindingTargetFromSelection } from '@/workflow-builder/_pure/interaction/selection';
import { createActionError } from '@/workflow-builder/_pure/model/error';
import {
    beginExecution,
} from '@/workflow-builder/_pure/model/executionState';
import { buildRuntimeBooleanResource, buildRuntimeNaturalResource, parseNaturalInput } from '@/workflow-builder/_pure/model/toolStepDraft';
import { SECTION_NATURAL_INPUT } from '@/workflow-builder/_data/overlay';
import { dispatchAction } from '@/workflow-builder/model/commands';
import type { Config } from '@/workflow/_contract/config';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { RuntimeState } from '@/workflow-builder/_contract/runtime';
import type { RunWorkflowCapabilities } from '@/workflow-builder/_contract/runWorkflow';

export {
    buildOverlayModel,
    resolveActionFromOverlayActionId,
};

function withBindMaterializedError(state: RuntimeState, message: string): RuntimeState {
    return {
        ...state,
        error: createActionError('bind_materialized_resource', message),
    };
}

export function reduceOverlayIntent(args: {
    config: Config;
    runtimeState: RuntimeState;
    intent: SpaceOverlayIntent;
    cosmosData: CosmosDataModel;
} & RunWorkflowCapabilities): RuntimeState {
    if (args.config.adapterMode !== 'builder') return args.runtimeState;
    const currentRuntimeState = args.runtimeState;
    if (args.intent.overlayIntentKind === 'action-triggered') {
        if (args.intent.actionId === ACTION_OVERVIEW) return requestWorkflowOverview(currentRuntimeState);
        if (args.intent.actionId === ACTION_FREE) return { ...currentRuntimeState, pendingOverviewNavigation: null };
    }

    const execution = currentRuntimeState.executionState;
    if (execution.executionStateKind === 'running' && execution.localInput) {
        const localInput = execution.localInput;
        if (args.intent.overlayIntentKind === 'text-changed' && args.intent.sectionId === SECTION_RUNTIME_INPUT) {
            if (localInput.submission) return currentRuntimeState;
            return { ...currentRuntimeState, error: null, executionState: {
                ...execution, localInput: { ...localInput, text: args.intent.value },
            } };
        }
        if (args.intent.overlayIntentKind === 'action-triggered'
            && [ACTION_SUBMIT_RUNTIME_NATURAL, ACTION_SUBMIT_RUNTIME_TRUE, ACTION_SUBMIT_RUNTIME_FALSE].includes(args.intent.actionId)) {
            if (localInput.submission) return currentRuntimeState;
            const natural = args.intent.actionId === ACTION_SUBMIT_RUNTIME_NATURAL;
            if (natural !== (localInput.requirement.resourceTypeHandle === 'TYPE-Natural')) return currentRuntimeState;
            const projection = natural ? parseNaturalInput(localInput.text) : args.intent.actionId === ACTION_SUBMIT_RUNTIME_TRUE;
            if (projection === null) {
                return { ...currentRuntimeState, error: createActionError('run_workflow', 'Runtime input requires a non-negative integer.') };
            }
            return { ...currentRuntimeState, error: null, executionState: {
                ...execution, localInput: { ...localInput,
                    submission: { requestId: localInput.requirement.requestId, projection },
                },
            } };
        }
    }
    if (execution.executionStateKind === 'running') {
        if (args.intent.overlayIntentKind !== 'action-triggered' || args.intent.actionId !== ACTION_DISCARD_WORKFLOW) {
            return currentRuntimeState;
        }
    }

    if (args.intent.overlayIntentKind === 'text-changed' && args.intent.sectionId === SECTION_NATURAL_INPUT) {
        return {
            ...currentRuntimeState,
            naturalBindingInputText: args.intent.value,
        };
    }

    if (args.intent.overlayIntentKind !== 'action-triggered') {
        return currentRuntimeState;
    }

    const selectedBindingTarget = selectBindingTargetFromSelection({
        runtimeState: currentRuntimeState,
        cosmosData: args.cosmosData,
    });

    if (isBooleanBindingAction(args.intent.actionId)) {
        const targetGuardError = getSelectedBindingTargetGuardError({
            selectedBindingTarget,
            expectedResourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Boolean,
            expectedTypeLabel: 'Boolean',
        });
        if (targetGuardError) {
            return withBindMaterializedError(currentRuntimeState, targetGuardError);
        }

        return dispatchAction({
            config: args.config,
            runtimeState: currentRuntimeState,
            cosmosData: args.cosmosData,
            action: {
                command: 'bind_materialized_resource',
                materializedResource: {
                    ...buildRuntimeBooleanResource(args.intent.actionId === ACTION_BIND_BOOLEAN_TRUE),
                    workflowStateEntryKind: 'materializedResource',
                },
            },
        });
    }

    if (isNaturalBindingAction(args.intent.actionId)) {
        const targetGuardError = getSelectedBindingTargetGuardError({
            selectedBindingTarget,
            expectedResourceTypeHandle: CONSTANTS.Handles.ResourceTypes.TYPE_Natural,
            expectedTypeLabel: 'Natural',
        });
        if (targetGuardError) {
            return withBindMaterializedError(currentRuntimeState, targetGuardError);
        }

        const parsedNatural = parseNaturalInput(currentRuntimeState.naturalBindingInputText);
        if (parsedNatural === null) {
            return withBindMaterializedError(currentRuntimeState, 'Natural binding requires a non-negative integer.');
        }

        return dispatchAction({
            config: args.config,
            runtimeState: currentRuntimeState,
            cosmosData: args.cosmosData,
            action: {
                command: 'bind_materialized_resource',
                materializedResource: {
                    ...buildRuntimeNaturalResource(parsedNatural),
                    workflowStateEntryKind: 'materializedResource',
                },
            },
        });
    }

    const resolvedAction = resolveActionFromOverlayActionId(args.intent.actionId);
    if (!resolvedAction) {
        return currentRuntimeState;
    }

    if (resolvedAction.command === 'run_workflow') {
        if (currentRuntimeState.phase !== 'workflow_ready' || currentRuntimeState.draft !== null) {
            return {
                ...currentRuntimeState,
                error: createActionError(resolvedAction.command, 'Workflow must be ready before running.'),
            };
        }
        const updateRuntimeState = args.config.runtimeCallbacks?.onRuntimeStateChange;
        const executeWorkflow = args.executeWorkflow;
        const executionMode = args.intent.actionId === ACTION_RUN_REMOTE ? 'remote' : 'local';
        const handoffToRunner = args.handoffToRunner;
        if (!(executionMode === 'remote' ? executeWorkflow : args.createLocalSession) || !updateRuntimeState || !handoffToRunner) {
            return {
                ...currentRuntimeState,
                error: createActionError(resolvedAction.command, 'Workflow execution runtime callbacks are unavailable.'),
            };
        }
        let canonicalWorkflow: Workflow;
        let writeLocalWorkflowTrace: boolean;
        try {
            writeLocalWorkflowTrace = requireConfig(args.config).adapterModeConfig.writeLocalWorkflowTrace ?? true;
            canonicalWorkflow = mutableWorkflowToWorkflow(currentRuntimeState.mutableWorkflow);
        } catch (error) {
            return {
                ...currentRuntimeState,
                error: createActionError(
                    resolvedAction.command,
                    error instanceof Error ? error.message : 'Could not canonicalize workflow for run.',
                ),
            };
        }

        const execution = beginExecution(currentRuntimeState);
        if (execution === undefined) return currentRuntimeState;
        return {
            ...execution.runtimeState,
            executionState: {
                executionStateKind: 'running',
                requestId: execution.requestId,
                request: { workflow: canonicalWorkflow, writeLocalWorkflowTrace, executionMode },
            },
        };
    }

    if (resolvedAction.command === 'dispatch_workflow') {
        return {
            ...currentRuntimeState,
            error: createActionError(resolvedAction.command, 'Dispatch is not wired yet.'),
        };
    }

    return dispatchAction({
        config: args.config,
        runtimeState: currentRuntimeState,
        cosmosData: args.cosmosData,
        action: resolvedAction,
    });
}
